import { isEmpty, orderBy } from 'lodash';
import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import { Undoable } from '@medsurf/flat-decorators';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '../../internal';
import { validate } from 'uuid';


export const PAGE_ENTITY_TOKEN = new StateToken<PageEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.PAGE_ENTITY);

export type PageEntityStateModel = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.PageEntityModels.PageEntityType>;

@State<PageEntityStateModel>({
  name: PAGE_ENTITY_TOKEN,
  defaults: {
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class PageEntityState extends HistoryEntityBaseState<FlatModels.PageEntityModels.PageEntityType, PageEntityStateModel> {
  //<editor-fold desc="Selectors">

  @Selector([PageEntityState])
  static override typedEntities$(state: PageEntityStateModel): { [id: string]: FlatModels.PageEntityModels.PageEntityType } {
    return HistoryEntityBaseState.typedEntities$(state);
  }

  @Selector([PageEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType }): FlatModels.PageEntityModels.PageEntityType[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }

  @Selector([PageEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }

  @Selector([PageEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }

  //<editor-fold desc="Slide">

  @Selector([
    PageEntityState.typedEntities$,
    FlatStates.NodeEntityState.currentSelectedElearningNode$
  ])
  static currentSelectedElearningPage$(typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType },
                                       elearningNode: FlatModels.NodeEntityModels.NodeEntityType) {
    return elearningNode && (typedEntities[elearningNode.page as unknown as string]) || null;
  }

  @Selector([
    PageEntityState.typedEntities$,
    FlatStates.NodeEntityState.currentSelectedSubjectNode$
  ])
  static currentSelectedSubjectPage$(typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType },
                                     subjectNode: FlatModels.NodeEntityModels.NodeEntityType) {
    return subjectNode && (typedEntities[subjectNode.page as unknown as string]) || null;
  }

  @Selector([
    PageEntityState.typedEntities$,
    FlatStates.NodeEntityState.currentSelectedChapterNode$
  ])
  static currentSelectedChapterPage$(typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType },
                                     chapterNode: FlatModels.NodeEntityModels.NodeEntityType) {
    return chapterNode && (typedEntities[chapterNode.page as unknown as string]) || null;
  }

  @Selector([PageEntityState.currentSelectedChapterPage$])
  static currentSelectedChapterPageType$(chapterPage: FlatModels.PageEntityModels.PageEntityType) {
    return chapterPage && chapterPage.type || null;
  }

  @Selector([
    PageEntityState.typedEntities$,
    FlatStates.NodeEntityState.currentSelectedBlockNode$
  ])
  static currentSelectedBlockPage$(typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType },
                                   blockNode: FlatModels.NodeEntityModels.NodeEntityType) {
    return blockNode && (typedEntities[blockNode.page as unknown as string]) || null;
  }

  @Selector([
    FlatStates.NodeEntityState.currentSelectedElearningNode$,
    FlatStates.NodeEntityState.currentSelectedSubjectNode$,
    FlatStates.NodeEntityState.currentSelectedChapterNode$,
    FlatStates.NodeEntityState.currentSelectedBlockNode$,
    FlatStates.NodeEntityState.currentSelectedSlideNode$,
    PageEntityState.typedEntities$,
    FlatStates.NodeEntityState.typedRouteElements$,
  ])
  static currentSelectedBreadcrumbs$(elearningNode: FlatModels.NodeEntityModels.NodeEntityType,
                                     subjectNode: FlatModels.NodeEntityModels.NodeEntityType,
                                     chapterNode: FlatModels.NodeEntityModels.NodeEntityType,
                                     blockNode: FlatModels.NodeEntityModels.NodeEntityType,
                                     slideNode: FlatModels.NodeEntityModels.NodeEntityType,
                                     typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType },
                                     typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }) {
    const breadcrumbs = [];
    if (elearningNode) {
      breadcrumbs.push({
        label: typedEntities[elearningNode.page]?.title || '',
        routerLink: '/' + typedRouteElements[elearningNode.id].path,
      });
    }
    if (subjectNode) {
      breadcrumbs.push({
        label: typedEntities[subjectNode.page]?.title || '',
        routerLink: '/' + typedRouteElements[subjectNode.id].path,
      });
    }
    if (chapterNode) {
      breadcrumbs.push({
        label: typedEntities[chapterNode.page]?.title || '',
        routerLink: '/' + typedRouteElements[chapterNode.id].path,
      });
    }
    if (blockNode) {
      breadcrumbs.push({
        label: typedEntities[blockNode.page]?.title || '',
        routerLink: '/' + typedRouteElements[blockNode.id].path,
      });
    }
    if (slideNode) {
      breadcrumbs.push({
        label: '#' + (blockNode.children.findIndex(c => c === slideNode.id) + 1 || 1) + ' - ' + typedEntities[slideNode.page]?.title || '',
        routerLink: '/' + typedRouteElements[slideNode.id].path,
      });
    }
    
    // Edit Last Entry
    const lastElement = breadcrumbs.pop();
    if (lastElement) {
      breadcrumbs.push({
        label: lastElement.label,
        styleClass: 'font-bold'
      });
    }
    
    return breadcrumbs;
  }

  @Selector([
    FlatStates.NodeEntityState.selectedNodeAndParentsSettings$,
    PageEntityState.typedEntities$,
    FlatStates.NodeEntityState.typedRouteElements$,
  ])
  static currentNodeBreadcrumbs$(selectedNodeAndParentsSettings: ReturnType<typeof FlatStates.NodeEntityState.selectedNodeAndParentsSettings$>,
                                 typedEntities: ReturnType<typeof PageEntityState.typedEntities$>,
                                 typedRouteElements: ReturnType<typeof FlatStates.NodeEntityState.typedRouteElements$>) {

    if (!selectedNodeAndParentsSettings || !typedEntities || isEmpty(typedEntities) || !typedRouteElements || isEmpty(typedRouteElements)) return null;
    const breadcrumbs: { label: string, routerLink: string, pageId: string }[] = [];

    const { node, currentSettings, parentsSettings } = selectedNodeAndParentsSettings;
    const settings = currentSettings;
    const iteratePages = [{node, settings }, ...parentsSettings];

    for (const page of iteratePages) {
      if (page.settings.combinedWithDefaults.hideBreadcrumbLink) {
        continue;
      }
      const { page: pageId, id: nodeId } = page.node;
      const routeElement = typedRouteElements[nodeId];
      if (routeElement?.depth >= 4) {
        continue;
      }
      breadcrumbs.unshift({
        label: typedEntities[pageId]?.title || '',
        routerLink: '/' + routeElement?.path,
        pageId: page.node.page,
      });
    }
    return breadcrumbs;
  }

  @Selector([
    PageEntityState.currentSelectedElearningPage$,
    PageEntityState.currentSelectedSubjectPage$,
    PageEntityState.currentSelectedChapterPage$,
    PageEntityState.currentSelectedBlockPage$,
    PageEntityState.currentSelectedSlidePage$
  ])
  static currentSelectedSlideTitle$(elearningPage: FlatModels.PageEntityModels.PageEntityType,
                                    subjectPage: FlatModels.PageEntityModels.PageEntityType,
                                    chapterPage: FlatModels.PageEntityModels.PageEntityType,
                                    blockPage: FlatModels.PageEntityModels.PageEntityType,
                                    slidePage: FlatModels.PageEntityModels.PageEntityType) {
    const titleArray: string[] = [];
    if (slidePage && slidePage.title) {
      titleArray.push(slidePage.title);
    }
    if (blockPage && blockPage.title) {
      titleArray.push(blockPage.title);
    }
    if (chapterPage && chapterPage.title) {
      titleArray.push(chapterPage.title);
    }
    if (subjectPage && subjectPage.title) {
      titleArray.push(subjectPage.title);
    }
    if (elearningPage && elearningPage.title) {
      titleArray.push(elearningPage.title);
    }
    return titleArray.join(' | ');
  }

  @Selector([
    PageEntityState.typedEntities$,
    FlatStates.NodeEntityState.currentSelectedSlideNode$,
    FlatStates.NavigationControlState.slideIdSlug$
  ])
  static currentSelectedSlidePage$(typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType },
                                   slideNode: FlatModels.NodeEntityModels.NodeEntityType,
                                   slideIdSlug: string | null) {
    if (slideNode) {
      return typedEntities[slideNode.page as unknown as string] as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training || null;
    }

    // Enable use of pageId
    if (slideIdSlug && validate(slideIdSlug)) {
      return typedEntities[slideIdSlug] as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training || null;
    }

    // Enable use of slideId
    if (slideIdSlug && /^[0-9]+$/.test(slideIdSlug)) {
      return Object.values(typedEntities).find(e => (e as any).slideId === Number.parseInt(slideIdSlug)) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training || null;
    }
    return slideIdSlug && typedEntities[slideIdSlug] as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training || null;
  }

  @Selector([
    PageEntityState.typedEntities$,
    FlatStates.NodeEntityState.currentSelectedNode$,
    FlatStates.NavigationControlState.slideIdSlug$
  ])
  static currentSelectedPage$(typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType },
                              node: FlatModels.NodeEntityModels.NodeEntityType,
                              slideIdSlug: string | null) {
    if (node) {
      return (typedEntities[node.page as unknown as string]) || null;
    }
    // Enable use of slideId
    if (slideIdSlug && /^[0-9]+$/.test(slideIdSlug)) {
      return Object.values(typedEntities).find(e => (e as any).slideId === Number.parseInt(slideIdSlug)) || null;
    }
    return slideIdSlug && typedEntities[slideIdSlug] || null;
  }
  
  @Selector([
    PageEntityState.entities$,
    FlatStates.NavigationControlState.pageSlug$,
  ])
  static currentSelectedInfoPage$(pages: FlatModels.PageEntityModels.PageEntityType[],
                                  pageSlug: string | null) {
    if (!pageSlug) return null;

    const page = pages.find(p => p.title.toLowerCase() === pageSlug.toLowerCase() && p.type === 'InfoPage') as FlatModels.PageEntityModels.InfoPage;
    if (!page) return null;

    return page;
  }

  @Selector([PageEntityState.currentSelectedPage$])
  static isBlock$(currentSelectedPage: FlatModels.PageEntityModels.PageEntityType) {
    return currentSelectedPage
        && (<string[]>[FlatModels.PageEntityModels.PageType.enum.Grid, FlatModels.PageEntityModels.PageType.enum.Case, FlatModels.PageEntityModels.PageType.enum.QuizRun]).includes(currentSelectedPage.type);
  }

  @Selector([PageEntityState.currentSelectedPage$])
  static isQuizStart$(currentSelectedPage: FlatModels.PageEntityModels.PageEntityType) {
    return <string>FlatModels.PageEntityModels.PageType.enum.QuizStart === currentSelectedPage?.type;
  }

  @Selector([PageEntityState.currentSelectedSlidePage$])
  static currentSelectedSlideSidebarSize$(currentSelectedSlidePage: FlatModels.PageEntityModels.Slide) {
    return Number(currentSelectedSlidePage?.sideBarSize) ?? 3;
  }

  @Selector([PageEntityState.currentSelectedSlideSidebarSize$])
  static currentSelectedSlideMainSize$(currentSelectedSlideSidebarSize: number) {
    return 12 - currentSelectedSlideSidebarSize;
  }

  @Selector([PageEntityState.currentSelectedSlideSidebarSize$])
  static currentSelectedSlideSidebarFullSize$(currentSelectedSlideSidebarSize: number) {
    return currentSelectedSlideSidebarSize >= 12;
  }

  @Selector([PageEntityState.currentSelectedSlideSidebarSize$])
  static currentSelectedSlideMainFullSize$(currentSelectedSlideSidebarSize: number) {
    return currentSelectedSlideSidebarSize <= 0;
  }

  //</editor-fold>

  @Selector([PageEntityState.currentSelectedSubjectPage$])
  static themeColor$(subjectPage: FlatModels.PageEntityModels.PageEntityType) {
    return subjectPage && (subjectPage as FlatModels.PageEntityModels.Folder).themeColor || 'black';
  }
  
  @Selector([
    PageEntityState.typedEntities$,
    FlatStates.NodeEntityState.currentSelectedSlideNodes$
  ])
  static currentSelectedTypedAuthorSlideNodes$(typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType },
                                               nodes: FlatModels.NodeEntityModels.NodeEntityType[]) {
    if (nodes) {
      return nodes.reduce((arr, node) => {
        const page = typedEntities[node.page] as FlatModels.PageEntityModels.Slide;
        if (page) {
          arr[node.id] = node;
        }
        return arr;
      }, {} as { [id: string]: FlatModels.NodeEntityModels.NodeEntityType });
    }
    return [];
  }
  
  @Selector([PageEntityState.currentSelectedTypedAuthorSlideNodes$])
  static currentSelectedAuthorSlideNodes$(currentSelectedTypedAuthorSlideNodes: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) {
    const filteredNodes = structuredClone(orderBy(Object.values(currentSelectedTypedAuthorSlideNodes), (a) => a.position || 0, 'asc'));
    for (let i = 0; i < filteredNodes.length; i++) {
      filteredNodes[i].position = i;
    }
    return filteredNodes;
  }

  @Selector([
    PageEntityState.currentSelectedAuthorSlideNodes$,
    FlatStates.NodeEntityState.currentSelectedSlideNode$
  ])
  static currentSelectedAuthorSlideIndex$(currentSelectedAuthorSlideNodes: FlatModels.NodeEntityModels.NodeEntityType[],
                                          currentSelectedSlideNode: FlatModels.NodeEntityModels.SlideNode) {
    if (currentSelectedSlideNode && Array.isArray(currentSelectedAuthorSlideNodes)) {
      const selectedSlideNode = currentSelectedAuthorSlideNodes.find(ss => ss.id === currentSelectedSlideNode.id);
      if (selectedSlideNode) {
        return Number.isInteger(selectedSlideNode.position) ? selectedSlideNode.position : null;
      }
    }
    return null;
  }
  
  @Selector([
    PageEntityState.currentSelectedAuthorSlideIndex$,
    PageEntityState.currentSelectedAuthorSlideNodes$
  ])
  static previousAuthorSlideNode$(currentSelectedAuthorSlideIndex: number | null,
                                  currentSelectedAuthorSlideNodes: FlatModels.NodeEntityModels.SlideNode[]) {
    if (currentSelectedAuthorSlideIndex !== null && Number.isInteger(currentSelectedAuthorSlideIndex) && Array.isArray(currentSelectedAuthorSlideNodes)) {
      if (currentSelectedAuthorSlideIndex > 0) {
        return currentSelectedAuthorSlideNodes.find(sn => sn.position === (currentSelectedAuthorSlideIndex - 1)) || null;
      }
    }
    return null;
  }

  @Selector([
    PageEntityState.previousAuthorSlideNode$,
    FlatStates.NavigationControlState.blockUrl$
  ])
  static previousAuthorSlideUrl$(previousAuthorSlideNode: FlatModels.NodeEntityModels.SlideNode | null,
                                 blockUrl: string | null) {
    if (blockUrl) {
      return previousAuthorSlideNode ? blockUrl + '/' + previousAuthorSlideNode.route : blockUrl;
    }
    return null;
  }

  @Selector([
    PageEntityState.currentSelectedAuthorSlideIndex$,
    PageEntityState.currentSelectedAuthorSlideNodes$
  ])
  static nextAuthorSlideNode$(currentSelectedAuthorSlideIndex: number | null,
                              currentSelectedAuthorSlideNodes: FlatModels.NodeEntityModels.SlideNode[]) {
    if (currentSelectedAuthorSlideIndex !== null && Number.isInteger(currentSelectedAuthorSlideIndex) && Array.isArray(currentSelectedAuthorSlideNodes)) {
      if (currentSelectedAuthorSlideIndex > -1 && currentSelectedAuthorSlideIndex <= currentSelectedAuthorSlideNodes.length) {
        return currentSelectedAuthorSlideNodes.find(sn => sn.position ===(currentSelectedAuthorSlideIndex + 1)) || null;
      }
    }
    return null;
  }
  
  @Selector([
    PageEntityState.nextAuthorSlideNode$,
    FlatStates.NavigationControlState.blockUrl$
  ])
  static nextAuthorSlideUrl$(nextAuthorSlideNode: FlatModels.NodeEntityModels.SlideNode | null,
                             blockUrl: string | null) {
    if (blockUrl) {
      return nextAuthorSlideNode ? blockUrl + '/' + nextAuthorSlideNode.route : blockUrl;
    }
    return null;
  }

  @Selector([
    PageEntityState.typedEntities$,
    FlatStates.NodeEntityState.currentSelectedSlideNodes$
  ])
  static currentSelectedTypedSlideNodesFiltered$(typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType },
                                                 nodes: FlatModels.NodeEntityModels.NodeEntityType[]) {
		/* TODO ANDREA */
    if (nodes) {
      return nodes.reduce((arr, node) => {
        const page = typedEntities[node.page] as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
        if (page && page.type !== FlatModels.PageEntityModels.PageType.enum.Training && !node.hidden) {
          arr[node.id] = node;
        }
        return arr;
      }, {} as { [id: string]: FlatModels.NodeEntityModels.NodeEntityType });
    }
    return [];
	  /* TODO THOMAS * /
	  if (nodes) {
      const filteredNodes = nodes.filter(n => {
        const page = typedEntities[n.page as unknown as string] as FlatModels.PageEntityModels.Slide;
        return page && page.type !== FlatModels.PageEntityModels.PageType.Training && !page.hidden;
      }).sort((a, b) => (a.position || 0) - (b.position || 0));
      // TODO check if we want to sort and recalculate position only, when we filter some nodes out
      for (let i = 0; i < filteredNodes.length; i++) {
        filteredNodes[i] = {...filteredNodes[i], position: i};
      }
      return filteredNodes;
    }
    return [];
    /**/
  }

  @Selector([PageEntityState.currentSelectedTypedSlideNodesFiltered$])
  static currentSelectedSlideNodesFiltered$(currentSelectedTypedSlideNodesFiltered: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) {
    const filteredNodes = structuredClone(orderBy(Object.values(currentSelectedTypedSlideNodesFiltered), (a) => a.position || 0, 'asc'));
    for (let i = 0; i < filteredNodes.length; i++) {
      filteredNodes[i].position = i;
    }
    return filteredNodes;
  }

  @Selector([
    PageEntityState.currentSelectedSlideNodesFiltered$,
    FlatStates.NodeEntityState.currentSelectedSlideNode$
  ])
  static currentSelectedSlideIndex$(currentSelectedSlideNodesFiltered: FlatModels.NodeEntityModels.NodeEntityType[],
                                    currentSelectedSlideNode: FlatModels.NodeEntityModels.SlideNode) {
    if (currentSelectedSlideNode && Array.isArray(currentSelectedSlideNodesFiltered)) {
      const selectedSlideNode = currentSelectedSlideNodesFiltered.find(ss => ss.id === currentSelectedSlideNode.id);
      if (selectedSlideNode) {
        return Number.isInteger(selectedSlideNode.position) ? selectedSlideNode.position : null;
      }
    }
    return null;
  }

  @Selector([
    PageEntityState.currentSelectedSlideIndex$,
    PageEntityState.currentSelectedSlideNodesFiltered$
  ])
  static currentSelectedSlidePercentage$(currentSelectedSlideIndex: number | null,
                                         currentSelectedSlideNodesFiltered: FlatModels.NodeEntityModels.NodeEntityType[]) {
    if (currentSelectedSlideIndex !== null && Array.isArray(currentSelectedSlideNodesFiltered)) {
      const item = 1 / currentSelectedSlideNodesFiltered.length;
      return (item * (currentSelectedSlideIndex + 1)) - item / 2;
    }
    return null;
  }

  @Selector([
    PageEntityState.currentSelectedSlideIndex$,
    PageEntityState.currentSelectedSlideNodesFiltered$
  ])
  static previousSlideNode$(currentSelectedSlideIndex: number | null,
                            currentSelectedSlideNodesFiltered: FlatModels.NodeEntityModels.SlideNode[]) {
    if (currentSelectedSlideIndex !== null && Number.isInteger(currentSelectedSlideIndex) && Array.isArray(currentSelectedSlideNodesFiltered)) {
      if (currentSelectedSlideIndex > 0) {
        return currentSelectedSlideNodesFiltered.find(sn => sn.position === (currentSelectedSlideIndex - 1)) || null;
      }
    }
    return null;
  }

  @Selector([
    PageEntityState.previousSlideNode$,
    FlatStates.NavigationControlState.blockUrl$
  ])
  static previousSlideUrl$(previousSlideNode: FlatModels.NodeEntityModels.SlideNode | null,
                           blockUrl: string | null) {
    if (blockUrl) {
      return previousSlideNode ? blockUrl + '/' + previousSlideNode.route : blockUrl;
    }
    return null;
  }

  @Selector([
    PageEntityState.currentSelectedSlideIndex$,
    PageEntityState.currentSelectedSlideNodesFiltered$
  ])
  static nextSlideNode$(currentSelectedSlideIndex: number | null,
                        currentSelectedSlideNodesFiltered: FlatModels.NodeEntityModels.SlideNode[]) {
    if (currentSelectedSlideIndex !== null && Number.isInteger(currentSelectedSlideIndex) && Array.isArray(currentSelectedSlideNodesFiltered)) {
      if (currentSelectedSlideIndex > -1 && currentSelectedSlideIndex <= currentSelectedSlideNodesFiltered.length) {
        return currentSelectedSlideNodesFiltered.find(sn => sn.position ===(currentSelectedSlideIndex + 1)) || null;
      }
    }
    return null;
  }

  @Selector([
    PageEntityState.nextSlideNode$,
    FlatStates.NavigationControlState.blockUrl$
  ])
  static nextSlideUrl$(nextSlideNode: FlatModels.NodeEntityModels.SlideNode | null,
                       blockUrl: string | null) {
    if (blockUrl) {
      return nextSlideNode ? blockUrl + '/' + nextSlideNode.route : blockUrl;
    }
    return null;
  }

  @Selector([
    FlatStates.NodeEntityState.entities$,
    PageEntityState.typedEntities$
  ])
  static nodeTitleArray$(typedNodes$: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                         typedPages$: { [id: string]: FlatModels.PageEntityModels.PageEntityType }) {
    return Object.values(typedNodes$).reduce<{name: string, value: string}[]>((arr, node) => {
      const page = typedPages$[node.page];
      if (page) {
        arr.push({ name: page.title, value: node.id });
      }
      return arr;
    }, []);
  }

  static pageById$(id: string | null) {
    return createSelector([PageEntityState.typedEntities$], (typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType }) => {
      return id && typedEntities[id] || null;
    });
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.PageEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<PageEntityStateModel>,
                            action: FlatActions.PageEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.PageEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<PageEntityStateModel>,
                              action: FlatActions.PageEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.PageEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<PageEntityStateModel>,
                               action: FlatActions.PageEntityActions.ModifyEntitySuccess) {
    // Get entities
    const entities = structuredClone(state.getState().entities);
    const oldEntity = entities[action.saveSnapshotItem.entity.id];
    
    let hasChanges = false;
    if (action.saveSnapshotItem.entity.slideId && action.saveSnapshotItem.modifierType === 'ADD') {
      hasChanges = true;
      (oldEntity as FlatModels.PageEntityModels.Slide).slideId = action.saveSnapshotItem.entity.slideId;
    }

    // Check for changes in pdfs -> clear history
    if (
      (!(oldEntity as FlatModels.PageEntityModels.Slide).pdfMedia && action.saveSnapshotItem.entity.pdfMedia) ||
      (!(oldEntity as FlatModels.PageEntityModels.Slide).pdfMediaAuthor && action.saveSnapshotItem.entity.pdfMediaAuthor)
    ) {
      hasChanges = true;
      (oldEntity as FlatModels.PageEntityModels.Slide).pdfMedia = action.saveSnapshotItem.entity.pdfMedia;
      (oldEntity as FlatModels.PageEntityModels.Slide).pdfMediaAuthor = action.saveSnapshotItem.entity.pdfMediaAuthor;
      state.dispatch(new FlatActions.HistoryControlActions.ClearHistoryForEntity(action.saveSnapshotItem));
    }
    
    if (hasChanges) {
      // Patch State
      state.patchState({
        entities
      });
    }
  }

  @Action(FlatActions.PageEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<PageEntityStateModel>,
                                 action: FlatActions.PageEntityActions.ModifyEntitiesSuccess) {
    // Get entities
    const entities = structuredClone(state.getState().entities);
    
    // Iterate through the results
    let hasChanges = false;
    for (const saveSnapshotItem of action.saveSnapshotItems) {
      const oldEntity = entities[saveSnapshotItem.entity.id];
      if (saveSnapshotItem.entity.slideId && saveSnapshotItem.modifierType === 'ADD') {
        hasChanges = true;
        (oldEntity as FlatModels.PageEntityModels.Slide).slideId = saveSnapshotItem.entity.slideId;
      }

      // Check for changes in pdfs -> clear history
      if (
        (!(oldEntity as FlatModels.PageEntityModels.Slide).pdfMedia && saveSnapshotItem.entity.pdfMedia) ||
        (!(oldEntity as FlatModels.PageEntityModels.Slide).pdfMediaAuthor && saveSnapshotItem.entity.pdfMediaAuthor)
      ) {
        hasChanges = true;
        (oldEntity as FlatModels.PageEntityModels.Slide).pdfMedia = saveSnapshotItem.entity.pdfMedia;
        (oldEntity as FlatModels.PageEntityModels.Slide).pdfMediaAuthor = saveSnapshotItem.entity.pdfMediaAuthor;
        state.dispatch(new FlatActions.HistoryControlActions.ClearHistoryForEntity(saveSnapshotItem));
      }
    }
    
    if (hasChanges) {
      // Patch State
      state.patchState({
        entities: entities
      });
    }
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.PageEntityActions.AddEntity)
  @Undoable(
    FlatActions.PageEntityActions.AddEntity,
    PAGE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<PageEntityStateModel>,
                     action: FlatActions.PageEntityActions.AddEntity) {
    return super.addEntity(state, action)
  }

  @Action(FlatActions.PageEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.PageEntityActions.UpdateEntity,
    PAGE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<PageEntityStateModel>,
                        action: FlatActions.PageEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }

  @Action(FlatActions.PageEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.PageEntityActions.RemoveEntity,
    PAGE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<PageEntityStateModel>,
                        action: FlatActions.PageEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.PageEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<PageEntityStateModel>,
                        action: FlatActions.PageEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }

  @Action(FlatActions.PageEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<PageEntityStateModel>,
                        action: FlatActions.PageEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }

  @Action(FlatActions.PageEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<PageEntityStateModel>,
                            action: FlatActions.PageEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>

  @Action(FlatActions.PageEntityActions.AddVocabularyGroup)
  addVocabularyGroup({getState, patchState}: StateContext<PageEntityStateModel>,
                     {id, vocabularyGroup}: FlatActions.PageEntityActions.AddVocabularyGroup) {
    // Update Entity
    const entities = structuredClone(getState().entities);
    const entity = entities[id];

    if (entity.type === FlatModels.PageEntityModels.PageType.enum.Vocabulary) {
      const vocabulary = entity as FlatModels.PageEntityModels.Vocabulary;
      if (Array.isArray(vocabulary.vocabularyGroups)) {
        vocabulary.vocabularyGroups.push(vocabularyGroup.id);
      } else {
        vocabulary.vocabularyGroups = [vocabularyGroup.id];
      }

      patchState({
        entities
      });
    }
  }

  @Action(FlatActions.PageEntityActions.RemoveVocabularyGroup)
  removeVocabularyGroup({getState, patchState}: StateContext<PageEntityStateModel>,
                        {id, vocabularyGroup}: FlatActions.PageEntityActions.RemoveVocabularyGroup) {
    // Update Entity
    const entities = structuredClone(getState().entities);
    const entity = entities[id];

    if (entity.type === FlatModels.PageEntityModels.PageType.enum.Vocabulary) {
      const vocabulary = entity as FlatModels.PageEntityModels.Vocabulary;
      if (Array.isArray(vocabulary.vocabularyGroups)) {
        vocabulary.vocabularyGroups.filter(vg => vg !== vocabularyGroup.id);
      }

      patchState({
        entities
      });
    }
  }

  //</editor-fold>
}
